import { mergeWith, isFunction, isArray } from 'lodash-es';
import React, { PropsWithChildren, useMemo } from 'react';
import {
  DefaultTheme,
  useTheme,
  ThemeProvider as StyledThemeProvider,
} from 'styled-components';
import { PartialDeep } from 'type-fest';
import { darkTheme, defaultTheme } from 'theme';
import { flipTheme } from 'theme/utils';

export type ThemeCreator = (
  currentTheme: DefaultTheme,
) => PartialDeep<DefaultTheme>;

export type ThemeProviderProps = PropsWithChildren<{
  theme: PartialDeep<DefaultTheme> | ThemeCreator | 'dark' | 'light' | 'flip';
}>;

function targetThemeResolver(
  theme: PartialDeep<DefaultTheme> | ThemeCreator | 'dark' | 'light' | 'flip',
) {
  if (theme === 'dark') {
    return darkTheme;
  }

  if (theme === 'light') {
    return defaultTheme;
  }

  if (theme === 'flip') {
    return flipTheme;
  }

  return theme;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, theme }) => {
  // this will get the nearest theme above this provider
  const parentTheme = useTheme();

  const newTheme = useMemo(() => {
    const currentTheme = parentTheme ?? defaultTheme;
    const targetTheme = targetThemeResolver(theme);

    if (currentTheme === targetTheme) {
      return currentTheme;
    }

    const themeModifications = isFunction(targetTheme)
      ? targetTheme(currentTheme)
      : targetTheme;

    return mergeWith(
      {},
      currentTheme,
      themeModifications,
      (oldValue, newValue) => {
        if (isArray(oldValue) && isArray(newValue)) {
          return newValue;
        }

        return undefined;
      },
    );
  }, [parentTheme, theme]);

  return <StyledThemeProvider theme={newTheme}>{children}</StyledThemeProvider>;
};

export default ThemeProvider;
